import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../components/Modal.tsx';
import AccentSwatches from './AccentSwatches.tsx';
import PlanReview from '../routes/new-deck/PlanReview.tsx';
import { Paperclip, Plus, Trash, X } from '../lib/icons.tsx';
import { OptionRow, OptionRows, Segmented, Toggle } from '../components/Controls.tsx';
import {
  IMG_RE,
  isSidecarDoc,
  VERBATIM_CHIPS,
  KEY_RE,
  LEGACY_PPT_RE,
  KEYNOTE_UPLOAD_NOTICE,
  LEGACY_PPT_UPLOAD_NOTICE,
} from '../routes/new-deck/shared.tsx';
import type { Verbatim } from '../routes/new-deck/shared.tsx';
import type { DeckSection, PlanOutput, PlannedSlide } from '../../shared/deck.ts';
import type { EngineChoiceBlock, SectionEngine, SettingsPayload } from '../../shared/api.ts';
import { api } from '../lib/api.ts';
import type { NewSection } from '../editor/useDeckEditor.ts';

// A section brief is a document or notes — not a whole deck. Deck formats
// (pptx/ppsx/key/ppt) are rejected with a notice rather than treated as a brief.
const SECTION_DOC_RE = /\.(pdf|md|markdown|txt|docx)$/i;

/** Derive a manifest-safe section key from a display name (non-coders never
 *  see it). Lifted from the old inline AddSectionForm. */
function keyFromName(name: string, existingKeys: ReadonlySet<string>): string {
  let base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!/^[a-z]/.test(base)) base = 's-' + base;
  if (!base) base = 'section';
  let key = base;
  let i = 2;
  while (existingKeys.has(key) || key === 'intro' || key === 'closing') {
    key = `${base}-${i}`;
    i += 1;
  }
  return key;
}

/** Dedupe File arrays by name+size (re-picking the same file is a no-op). */
function dedupe(files: File[]): File[] {
  const seen = new Set<string>();
  return files.filter((f) => {
    const k = f.name + ':' + f.size;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * The New Section modal — styled like the New Deck modal. Two paths:
 *   • no content → create an EMPTY section (name + colour + position), the
 *     original behaviour.
 *   • content uploaded → PLAN the section's slides from it, review them
 *     (reorder / rename / delete), then build (draft-then-review).
 *
 * It owns no build lifecycle: on "Plan slides" it hands the section spec, a lite
 * answers object and the doc/image File arrays to the parent (useSectionBuild),
 * which runs the job and streams progress. When `planPhase` is set it shows the
 * shared PlanReview gate instead of the intake form.
 */
/** Where the photography comes from, as the CONTROL puts it. `Verbatim` is the
 *  chip roster's own union, imported alongside it. Both reach the server inside
 *  `answers`, imagery after the derivation in `submitBuild`. */
type Imagery = 'none' | 'use' | 'stock';

/** One engine as the Build engine row lists it — the same shape the new-deck
 *  wizard builds from the server's `build.engines` block. */
type EngineRow = { value: SectionEngine; label: string; available?: boolean; issue?: string; model?: string | null };

type SectionModalProps = {
  sections: DeckSection[];
  onCreateEmpty: (section: NewSection) => void;
  /** `answers` is what the planner reads; `guidance` is direction only, kept
   *  apart from the material so faithfulness never applies to it. */
  onBuild: (
    section: NewSection,
    answers: { verbatim: Verbatim; imagery: 'uploaded' | 'stock' | 'none' },
    docs: File[],
    imgs: File[],
    guidance: string,
    engine: SectionEngine,
  ) => void;
  onCancel: () => void;
  busy?: boolean;
  stockOk?: boolean;
  /** Plan-review gate (mirrors NewDeckModal). Null shows the intake form. */
  planPhase?: 'planning' | 'review' | null;
  plan?: PlanOutput | null;
  planBusy?: boolean;
  planErrors?: string[] | null;
  onApprovePlan: (slides: PlannedSlide[]) => void;
  onCancelReview?: () => void;
};

export default function SectionModal({
  sections,
  onCreateEmpty,
  onBuild,
  onCancel,
  busy = false,
  stockOk = false,
  planPhase = null,
  plan = null,
  planBusy = false,
  planErrors = null,
  onApprovePlan,
  onCancelReview,
}: SectionModalProps) {
  const contentSections = useMemo(() => (sections || []).filter((s) => s.key !== 'closing'), [sections]);
  const [name, setName] = useState('');
  const [color, setColor] = useState(() => (sections || []).find((s) => s.key === 'intro')?.color || 'ORANGE');
  const [insertAfter, setInsertAfter] = useState(contentSections[contentSections.length - 1]?.key || 'intro');
  // A section opens with a full-bleed cover (the SectionDivider). On by default;
  // unticking creates the section without it (noDivider).
  const [cover, setCover] = useState(true);

  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState('');
  // Guidance is direction for the AI ("what to make / focus on"), NOT words to
  // reproduce — kept separate from the content so the faithfulness choice only
  // ever applies to material the author wants preserved.
  const [guidance, setGuidance] = useState('');
  const [verbatim, setVerbatim] = useState<Verbatim>('light');
  const [imagery, setImagery] = useState<Imagery>('none');
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  // Which engine builds this section, and whether the alternatives can run.
  // Read from the same `build` block on GET /api/settings that the new-deck
  // wizard and the ⚙ drawer read, so the two engine rows can never disagree.
  const [engine, setEngine] = useState<SectionEngine>('claude');
  const [buildEngines, setBuildEngines] = useState<EngineChoiceBlock | null>(null);
  // Set once the author picks an engine themselves, so the probe landing late
  // can't undo a deliberate choice. Same guard as the wizard's `touched` ref.
  const enginePicked = useRef(false);
  useEffect(() => {
    api
      .get<SettingsPayload>('/api/settings')
      .then((d) => setBuildEngines(d?.build || null))
      .catch(() => {});
  }, []);
  // Start on the engine the SERVER says it can actually start, exactly as the
  // wizard does (routes/new-deck/NewDeckModal.tsx). `build.engine` is already
  // reduced by reduceToStartable() — never re-derive it here.
  //
  // Not cosmetic: hard-coding 'claude' as the opening value means that on a
  // server with no Claude credentials the row opens preselected on an option
  // the Segmented renders DISABLED, and Build then sends no `engine` field at
  // all (the client only names non-default engines), so the server reads Claude
  // and the build fails on a choice the author never made.
  useEffect(() => {
    const startable = buildEngines?.engine;
    if (!startable || enginePicked.current) return;
    if (startable === 'claude' || startable === 'fast' || startable === 'pi') setEngine(startable);
  }, [buildEngines]);

  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // Two content modes, mirroring the New Deck modal: 'upload' (drop a brief /
  // notes / images) or 'write' (paste the content in the form). Switching
  // clears the other mode's inputs so the content the planner sees always
  // matches what's on screen. Leaving both empty keeps the empty-section path.
  const [mode, setMode] = useState<'upload' | 'write'>('upload');
  function switchMode(next: 'upload' | 'write') {
    if (next === mode) return;
    if (next === 'write') {
      setDocFiles([]);
      setImageFiles([]);
      setFileNotice(null);
    } else {
      setNotes('');
    }
    setMode(next);
  }

  // Material = words/images to use (faithfulness applies). Guidance = direction
  // only. Either (or both) drives a build; both empty keeps the empty-section path.
  const hasMaterial = docFiles.length > 0 || imageFiles.length > 0 || notes.trim().length > 0;
  const hasGuidance = guidance.trim().length > 0;
  const hasAnyInput = hasMaterial || hasGuidance;
  // The roster carries the sentence that says what each answer MEANS, which is
  // what lets the control itself stay a short segmented row.
  const verbatimHint = VERBATIM_CHIPS.find((c) => c.value === verbatim)?.hint || '';

  // The engines this server can build on besides Claude, in the order they were
  // added. Gated exactly as the wizard gates them, on the engine's BLOCK
  // existing rather than on `.model`: an engine that cannot start says why
  // instead of disappearing. See NewDeckModal for the reasoning in full.
  const engineBlocks = buildEngines?.engines || {};
  const altEngines = [
    engineBlocks.fast && { value: 'fast' as SectionEngine, label: 'OpenCode', ...engineBlocks.fast },
    engineBlocks.pi && { value: 'pi' as SectionEngine, label: 'Pi', ...engineBlocks.pi },
  ].filter((e): e is EngineRow => Boolean(e));
  // Three segments no longer fit beside the label, so the row stacks and wraps.
  const engineRowWide = altEngines.length > 1;
  const blockedEngines = altEngines.filter((e) => !e.available && e.issue);

  // Default the imagery choice from what's uploaded.
  useEffect(() => {
    setImagery(imageFiles.length ? 'use' : 'none');
  }, [imageFiles.length]);

  // Escape closes (respect the review gate — the parent decides where that goes).
  const escHandler = useRef(onCancel);
  escHandler.current = onCancel;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') escHandler.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const thumbs = useMemo(() => imageFiles.map((f) => ({ f, url: URL.createObjectURL(f) })), [imageFiles]);
  useEffect(() => () => thumbs.forEach((t) => URL.revokeObjectURL(t.url)), [thumbs]);

  function addFiles(fileList: FileList | File[] | null) {
    const docs: File[] = [];
    const imgs: File[] = [];
    for (const f of fileList || []) {
      if (KEY_RE.test(f.name)) {
        setFileNotice(KEYNOTE_UPLOAD_NOTICE);
        continue;
      }
      if (LEGACY_PPT_RE.test(f.name)) {
        setFileNotice(LEGACY_PPT_UPLOAD_NOTICE);
        continue;
      }
      if (/\.(pptx|ppsx)$/i.test(f.name)) {
        setFileNotice('Slides aren’t a section brief: export the content as a PDF or paste it as notes.');
        continue;
      }
      if (IMG_RE.test(f.name)) imgs.push(f);
      else if (SECTION_DOC_RE.test(f.name)) {
        if (isSidecarDoc(f)) continue;
        docs.push(f);
      }
    }
    if (docs.length) setDocFiles((cur) => dedupe([...cur, ...docs]));
    if (imgs.length) setImageFiles((cur) => dedupe([...cur, ...imgs]));
  }
  const removeDoc = (f: File) => setDocFiles((cur) => cur.filter((x) => x !== f));
  const removeImg = (f: File) => setImageFiles((cur) => cur.filter((x) => x !== f));

  /** Uploaded docs plus a synthesised notes file when the user typed some. */
  function briefDocs(): File[] {
    const docs = [...docFiles];
    if (notes.trim()) docs.push(new File([notes], 'section-notes.md', { type: 'text/markdown' }));
    return docs;
  }

  function submitEmpty() {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    const existingKeys = new Set((sections || []).map((s) => s.key));
    onCreateEmpty({ key: keyFromName(trimmed, existingKeys), name: trimmed, color, insertAfter, noDivider: !cover });
  }

  function submitBuild() {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    const existingKeys = new Set((sections || []).map((s) => s.key));
    const section = { key: keyFromName(trimmed, existingKeys), name: trimmed, color, insertAfter, noDivider: !cover };
    // `use` only means anything with images to use, and `stock` only without
    // them — so the wire value is derived from both, never sent raw.
    const wireImagery: 'uploaded' | 'stock' | 'none' =
      imageFiles.length && imagery === 'use'
        ? 'uploaded'
        : !imageFiles.length && imagery === 'stock'
          ? 'stock'
          : 'none';
    const answers = { verbatim, imagery: wireImagery };
    // "None" with images in hand means keep MY pictures out of this section, so
    // withhold the files themselves. Sending them with imagery:'none' used to
    // be enough; it no longer is, because the intake places any upload it
    // receives regardless of the stock answer (see IntakeAnswers.imagery).
    onBuild(section, answers, briefDocs(), imagery === 'none' ? [] : imageFiles, guidance.trim(), engine);
  }

  // ---- Plan-review gate ----------------------------------------------------
  if (planPhase) {
    return (
      <Modal ariaLabel="New section" onClose={onCancel} className="modal--new-deck">
        <PlanReview
          planPhase={planPhase}
          plan={plan}
          busy={planBusy}
          errors={planErrors}
          onBack={onCancelReview}
          onApprove={onApprovePlan}
          onClose={onCancel}
          title="Review the section"
          planningTitle="Planning your section…"
          approveLabel="Build section"
          intro={
            plan
              ? `${plan.slides?.length || 0} slide${(plan.slides?.length || 0) === 1 ? '' : 's'} for this section. Reorder, rename or delete before it builds.`
              : ''
          }
        />
      </Modal>
    );
  }

  // ---- Intake form ---------------------------------------------------------
  return (
    <Modal ariaLabel="New section" onClose={onCancel} className="modal--new-deck">
      <header className="modal__header new-deck__head">
        <h2 className="modal__title" id="new-section-title">
          New section
        </h2>
        <span className="add-slide__spacer" />
        <button type="button" className="iconbtn" onClick={onCancel} aria-label="Close" title="Close">
          <X />
        </button>
      </header>

      <div className="modal__body new-deck__body">
        {/* ---------- The basics ---------- */}
        <section className="new-deck__section">
          <h3 className="settings__heading">The basics</h3>
          <label className="settings__field">
            <span>Section name</span>
            <input
              type="text"
              value={name}
              placeholder="What’s this section about?"
              maxLength={80}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="settings__field">
            <span>Colour</span>
            <AccentSwatches value={color} onPick={setColor} disabled={busy} />
          </div>
          <label className="settings__field">
            <span>Position</span>
            <select value={insertAfter} onChange={(e) => setInsertAfter(e.target.value)}>
              {contentSections.map((s) => (
                <option key={s.key} value={s.key}>
                  After {s.name || s.key}
                </option>
              ))}
            </select>
          </label>
          <OptionRows>
            <OptionRow
              label="Open with a cover"
              hint={
                cover
                  ? 'A full-bleed divider slide introduces the section.'
                  : 'The section starts straight into its first content slide.'
              }
            >
              <Toggle checked={cover} onChange={setCover} label="Open with a cover slide" disabled={busy} />
            </OptionRow>
          </OptionRows>
        </section>

        {/* ---------- Content (optional) ---------- */}
        <section className="new-deck__section">
          <h3 className="settings__heading">
            Content <span className="muted small">(optional, to build the slides for you)</span>
          </h3>

          <div className="new-deck__modes" role="tablist" aria-label="How to add content">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'upload'}
              className={'new-deck__mode' + (mode === 'upload' ? ' new-deck__mode--active' : '')}
              onClick={() => switchMode('upload')}
              disabled={busy}
            >
              <span className="new-deck__mode-name">Upload</span>
              <span className="new-deck__mode-sub">A brief, notes or images</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'write'}
              className={'new-deck__mode' + (mode === 'write' ? ' new-deck__mode--active' : '')}
              onClick={() => switchMode('write')}
              disabled={busy}
            >
              <span className="new-deck__mode-name">Write it</span>
              <span className="new-deck__mode-sub">Paste the content in the form</span>
            </button>
          </div>

          {mode === 'upload' && (
            <>
              <div
                className={'assets__drop new-deck__drop' + (dragOver ? ' assets__drop--over' : '')}
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  addFiles(e.dataTransfer?.files || []);
                }}
              >
                <Plus />
                <span>Drop a brief, notes or images here</span>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileRef.current?.click();
                  }}
                >
                  Choose files
                </button>
                <span className="muted small">PDF / Word / Markdown / text · PNG / JPG / WEBP / GIF / SVG / MP4</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.md,.txt,.markdown,.docx,image/*,video/mp4,video/webm"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>

              {fileNotice && (
                <p className="new-deck__file-notice muted small" role="status">
                  {fileNotice}{' '}
                  <button type="button" className="link" onClick={() => setFileNotice(null)}>
                    Dismiss
                  </button>
                </p>
              )}

              {docFiles.length > 0 && (
                <ul className="new-deck__files" role="list">
                  {docFiles.map((f) => (
                    <li key={f.name + f.size} className="new-deck__file">
                      <Paperclip />
                      <span className="new-deck__file-name">{f.name}</span>
                      <button
                        type="button"
                        className="iconbtn"
                        onClick={() => removeDoc(f)}
                        aria-label={`Remove ${f.name}`}
                      >
                        <Trash />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {thumbs.length > 0 && (
                <ul className="new-deck__thumbs" role="list">
                  {thumbs.map(({ f, url }) => (
                    <li key={f.name + f.size} className="new-deck__thumb" title={f.name}>
                      {/\.(mp4|webm|mov)$/i.test(f.name) ? (
                        <video src={url} muted loop playsInline />
                      ) : (
                        <img src={url} alt={f.name} />
                      )}
                      <button
                        type="button"
                        className="iconbtn new-deck__thumb-x"
                        onClick={() => removeImg(f)}
                        aria-label={`Remove ${f.name}`}
                      >
                        <Trash />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {mode === 'write' && (
            <textarea
              className="new-deck__notes"
              rows={5}
              placeholder="Paste the section’s content / notes here"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              autoFocus
            />
          )}
        </section>

        {/* ---------- Guidance (optional) — direction for the AI ---------- */}
        <section className="new-deck__section">
          <h3 className="settings__heading">
            Guidance for the AI <span className="muted small">(optional, what to make or focus on)</span>
          </h3>
          <textarea
            className="new-deck__notes"
            rows={2}
            placeholder="e.g. Provide resources for coffee training in the UK. The AI writes the slides from this."
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
          />
        </section>

        {/* ---------- Options (only meaningful with an input) ---------- */}
        {hasAnyInput && (
          <section className="new-deck__section">
            <h3 className="settings__heading">Options</h3>
            <OptionRows>
              {hasMaterial && (
                <OptionRow label="Your words" hint={verbatimHint} stack>
                  <Segmented
                    value={verbatim}
                    onChange={setVerbatim}
                    label="How faithfully to use your words"
                    wrap
                    options={VERBATIM_CHIPS.map((c) => ({ value: c.value, label: c.label, title: c.hint }))}
                  />
                </OptionRow>
              )}
              {/* Uploaded images beat stock: with pictures in hand the choice
                    is use them or not, and offering stock alongside would ask
                    the author to rank two things they never compared. With no
                    upload and no stock provider there is no choice to make at
                    all, so the row says why rather than showing one dead
                    option. */}
              <OptionRow
                label="Photography"
                hint={
                  imageFiles.length > 0
                    ? imagery === 'none'
                      ? 'Your images stay out of this section.'
                      : 'Your uploaded images, placed where they fit.'
                    : stockOk
                      ? imagery === 'stock'
                        ? 'Stock photos, only where they earn their place.'
                        : 'No photography in this section.'
                      : 'No stock provider configured: upload images to use photography.'
                }
              >
                {imageFiles.length > 0 ? (
                  <Segmented
                    value={imagery}
                    onChange={setImagery}
                    label="Photography"
                    options={[
                      { value: 'use', label: 'Use my images' },
                      { value: 'none', label: 'None' },
                    ]}
                  />
                ) : stockOk ? (
                  <Segmented
                    value={imagery}
                    onChange={setImagery}
                    label="Photography"
                    options={[
                      { value: 'stock', label: 'Stock photos' },
                      { value: 'none', label: 'None' },
                    ]}
                  />
                ) : null}
              </OptionRow>

              {/* Only the engines this server has CONFIGURED — a Claude-only
                    install must not be asked a question with one answer. One
                    that is configured but can't start right now stays visible
                    and disabled carrying the server's own reason, so the choice
                    can never fail on click. Mirrors the new-deck wizard's row;
                    a section spends exactly what a deck does. */}
              {altEngines.length > 0 && (
                <OptionRow
                  label="Build engine"
                  stack={engineRowWide}
                  hint={
                    engine === 'fast'
                      ? `${engineBlocks.fast?.model} plans and writes the section on OpenCode. Same brief, same playbook, a cheaper model. Always charged to the gateway key.`
                      : engine === 'pi'
                        ? `Pi plans and writes the section on ${engineBlocks.pi?.model || 'the model Settings names'}${engineBlocks.pi?.auth === 'subscription' ? ` on the ${engineBlocks.pi?.subscriptionProvider || 'subscription'} login` : engineBlocks.pi?.baseUrl ? ` at ${engineBlocks.pi.baseUrl}, metered` : ', metered'}.`
                        : `Claude plans and writes the section. The default, and what deck quality is tuned on. ${engineBlocks.claude?.auth === 'api' ? 'Charged to the Anthropic API key, at API rates.' : 'No cost per build.'}${blockedEngines.map((e) => ` ${e.issue}`).join('')}`
                  }
                >
                  <Segmented
                    value={engine}
                    onChange={(v) => {
                      enginePicked.current = true;
                      setEngine(v);
                    }}
                    label="Build engine"
                    wrap={engineRowWide}
                    options={[
                      // Built from the server block like the others, never
                      // hardcoded: Claude can be unavailable for a credential
                      // reason of its own, and a choice must not fail on click.
                      {
                        value: 'claude' as SectionEngine,
                        label: 'Claude',
                        disabled: engineBlocks.claude ? !engineBlocks.claude.available : false,
                        title: engineBlocks.claude?.issue || undefined,
                      },
                      ...altEngines.map((e) => ({
                        value: e.value,
                        label: e.label,
                        disabled: !e.available,
                        title: e.issue || undefined,
                      })),
                    ]}
                  />
                </OptionRow>
              )}
            </OptionRows>
          </section>
        )}
      </div>

      <footer className="modal__footer new-deck__foot">
        <span className="muted small new-deck__foot-hint">
          {hasAnyInput
            ? 'We plan the section’s slides from your input; you review them before anything is built.'
            : 'Creates an empty section. Add content or guidance above to have the slides built for you.'}
        </span>
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {hasAnyInput ? (
          <button type="button" className="btn btn--primary" onClick={submitBuild} disabled={busy || !name.trim()}>
            {busy ? 'Starting…' : 'Plan slides'}
          </button>
        ) : (
          <button type="button" className="btn btn--primary" onClick={submitEmpty} disabled={busy || !name.trim()}>
            Add section
          </button>
        )}
      </footer>
    </Modal>
  );
}
